import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import CaptainBlock from './CaptainBlock';
import EmailGateBlock from './EmailGateBlock';
import LeagueNameBlock from './LeagueNameBlock';
import LeagueSelectBlock from './LeagueSelectBlock';
import ProcessingBlock from './ProcessingBlock';
import RaceVerdictBlock from './RaceVerdictBlock';
import ReadinessBar from './shared/ReadinessBar';
import StakesBlock from './StakesBlock';
import TeamConfirmBlock from './TeamConfirmBlock';
import TeamImportBlock from './TeamImportBlock';
import TemplateTeamBlock from './TemplateTeamBlock';
import TopBanner from './shared/TopBanner';
import TransferBlock from './TransferBlock';
import VerdictBlock from './VerdictBlock';
import FunnelHeader from '@components/quiz/shared/FunnelHeader';
import StepProgressTopBar from '@components/quiz/shared/StepProgressTopBar';
import PaywallBlock from '@components/quiz/PaywallBlock';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import {
  deriveRaceMetrics,
  resolveRaceConfig
} from '@components/quiz/ReadinessProvider/utils/race-state';
import { buildReadinessPaywallContext } from '@components/quiz/ReadinessProvider/utils/paywall-context';
import { useFunnelEmailCapture } from '@hooks/useFunnelEmailCapture';
import { awaitEmailCaptureWithinBudget } from '@components/quiz/shared/await-email-capture';
import useQuizHashTracking from '@hooks/useQuizHashTracking';
import type { FplEntrySearchResult } from '@api-service/fpl-entries';
import {
  captureFunnelEmailCaptured,
  captureFunnelStarted,
  captureFunnelStepCompleted
} from '@lib/analytics/funnel-events';
import { INITIAL_QUIZ_STATE } from '@lib/page-utils/Quiz';
import {
  deriveProgressSteps,
  getCurrentProgressStep
} from '@lib/page-utils/Readiness/progress';
import type { AnalyticsFunnel } from '@lib/analytics/events';
import type {
  SanityQuizBlock,
  SanityQuizPage,
  SanityQuizTopBanner
} from '@lib/sanity/types';

type ReadinessFlowControllerProps = {
  quizPage: SanityQuizPage;
};

// Step blocks that show the sticky jeopardy/race bar (spec 6.1 steps
// 4-7; spec 6.2 wakes the race bar from league selection onwards). The
// email gate is excluded — it shows the score inline via LockedScoreCard.
const JEOPARDY_BAR_BLOCK_TYPES = new Set([
  'readinessCaptainBlock',
  'readinessTransferBlock',
  'readinessStakesBlock',
  'readinessLeagueSelectBlock'
]);

// Blocks reachable without an imported squad
const NO_TEAM_BLOCK_TYPES = new Set([
  'readinessTeamImportBlock',
  'readinessTemplateTeamBlock'
]);

const BUILDING_SCORE_LABEL = 'Building your score';

// The analytics funnel this controller drives (FFH-2372).
const READINESS_FUNNEL: AnalyticsFunnel = 'mini_league_readiness';

export type ReadinessStepNavigation = {
  goToNextStep: () => void;
  goToBlockType: (blockType: SanityQuizBlock['_type']) => void;
};

const getTopBanner = (
  block: SanityQuizBlock
): SanityQuizTopBanner | undefined =>
  'topBanner' in block ? block.topBanner : undefined;

/**
 * Drives the readiness step flows (pre- and post-GW1): one Sanity block
 * per step, branch routing for team acquisition and league targeting,
 * hash-based back/forward navigation, and the shared chrome (top bar,
 * banner, jeopardy/race bar).
 */
const ReadinessFlowController: FC<ReadinessFlowControllerProps> = ({
  quizPage
}) => {
  const { blocks, templateKey, readinessScoring, readinessRace } = quizPage;
  const { state, actions, isPostGw1, liveReadiness, liveRace } = useReadiness();
  const router = useRouter();

  const firstStepIndex = useMemo(
    () =>
      Math.max(
        0,
        blocks.findIndex((block) => block._type === 'readinessTeamImportBlock')
      ),
    [blocks]
  );

  const [currentIndex, setCurrentIndex] = useState(firstStepIndex);
  // Held on the provider rather than here: it is the paywall's Auth0
  // login_hint, and also the fallback identity the completion PATCH sends when
  // the session cookie does not travel, which the provider is what issues.
  // In-memory only — gone on refresh, like the flow.
  const capturedEmail = state.capturedEmail;

  // Each step is a pseudo page, so it should open at the top rather
  // than wherever the previous step was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentIndex]);

  // FFH-2372: session-latched inside the helper, so the refresh guard's
  // bounce back to the first step does not read as a second start.
  useEffect(() => {
    captureFunnelStarted(READINESS_FUNNEL, router.pathname);
  }, [router.pathname]);

  const emailCaptureMutation = useFunnelEmailCapture();

  const handleGoToStep = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useQuizHashTracking(blocks, currentIndex, handleGoToStep);

  // Hydrate the Sanity scoring/race tuning into the provider once
  useEffect(() => {
    actions.setScoringConfig(readinessScoring ?? null);
    actions.setRaceConfig(readinessRace ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readinessScoring, readinessRace]);

  const currentBlock = blocks[currentIndex];

  // Refresh/deep-link guard: in-memory state is gone after a reload, so
  // any step that needs a squad bounces back to the import step.
  useEffect(() => {
    if (!currentBlock) return;
    if (NO_TEAM_BLOCK_TYPES.has(currentBlock._type)) return;

    const needsTeamMeta =
      currentBlock._type === 'readinessTeamConfirmBlock' ||
      currentBlock._type === 'readinessLeagueSelectBlock';
    const isMissingState = needsTeamMeta
      ? state.teamMeta === null
      : state.team.length === 0;

    if (isMissingState) {
      setCurrentIndex(firstStepIndex);
    }
  }, [currentBlock, state.team.length, state.teamMeta, firstStepIndex]);

  const progressSteps = useMemo(() => deriveProgressSteps(blocks), [blocks]);
  const currentProgressStep = getCurrentProgressStep(
    progressSteps,
    currentIndex
  );

  const goToBlockType = useCallback(
    (blockType: SanityQuizBlock['_type']) => {
      const index = blocks.findIndex((block) => block._type === blockType);
      if (index !== -1) setCurrentIndex(index);
    },
    [blocks]
  );

  const goToNextStep = useCallback(() => {
    const currentGroup = getCurrentProgressStep(progressSteps, currentIndex);
    const nextGroup = progressSteps[currentGroup + 1];

    // FFH-2372: advancing means the visitor finished the step they were on.
    // Lateral branch jumps (goToBlockType) are not completions and stay
    // unmeasured on purpose. Past the final step currentGroup is
    // steps.length, so the lookup is undefined and nothing fires.
    const completedStep = progressSteps[currentGroup];
    if (completedStep) {
      captureFunnelStepCompleted(
        READINESS_FUNNEL,
        currentGroup,
        completedStep.groupId
      );
    }

    if (nextGroup) {
      setCurrentIndex(nextGroup.blockIndexes[0]);
      return;
    }

    // Past the final step group (e.g. verdict → trailing paywall block)
    if (currentIndex + 1 < blocks.length) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [progressSteps, currentIndex, blocks.length]);

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      // Trimmed once so the capture payload and the Auth0 login_hint
      // (capturedEmail -> paywall) agree; type="email" inputs usually
      // sanitise this already, but not reliably in every webview.
      const trimmedEmail = email.trim();
      const funnelSlug = templateKey || quizPage.slug.current;
      actions.setCapturedEmail(trimmedEmail);
      // FFH-2372: fired on submit, not on the mutation settling — the capture
      // request is allowed to fail without trapping the visitor, so gating
      // the event on it would under-count. Carries no email: no PII.
      captureFunnelEmailCaptured(READINESS_FUNNEL);

      // Awaited, unlike before. This request is what issues the
      // `_funnel_session` cookie, and the completion PATCH that follows needs
      // it to find the capture; letting the funnel run on while the capture is
      // still in flight is how a completion ends up with no cookie to report
      // against (FFH-2376).
      //
      // A failure does not trap the visitor: the funnel is theirs to finish,
      // and the completion still carries the email as its fallback identity.
      await awaitEmailCaptureWithinBudget(
        emailCaptureMutation
          .mutateAsync({ email: trimmedEmail, funnelSlug })
          .catch((error: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Funnel email capture failed', { funnelSlug, error });
          })
      );

      goToNextStep();
    },
    [emailCaptureMutation, templateKey, quizPage.slug, goToNextStep, actions]
  );

  const handleEntrySelected = useCallback(
    (entry: FplEntrySearchResult) => {
      // Fire the squad fetch immediately; the confirm card renders the
      // search result while it loads.
      actions.confirmTeamFromSearch(entry);
      goToBlockType('readinessTeamConfirmBlock');
    },
    [actions, goToBlockType]
  );

  const handleSearchAgain = useCallback(() => {
    actions.resetImportError();
    goToBlockType('readinessTeamImportBlock');
  }, [actions, goToBlockType]);

  const handleProcessingComplete = useCallback(() => {
    if (isPostGw1) {
      actions.calculateRaceVerdict();
      goToNextStep();
      return;
    }

    const verdictBlock = blocks.find(
      (block) => block._type === 'readinessVerdictBlock'
    );
    const oddsBands =
      verdictBlock && 'oddsBands' in verdictBlock
        ? verdictBlock.oddsBands ?? []
        : [];
    actions.calculateReadinessVerdict(oddsBands);
    goToNextStep();
  }, [isPostGw1, blocks, actions, goToNextStep]);

  if (!currentBlock) return null;

  const isJeopardyBarVisible = JEOPARDY_BAR_BLOCK_TYPES.has(currentBlock._type);
  const isEmailGate = currentBlock._type === 'readinessEmailGateBlock';
  const scoreReadyLabel =
    (isEmailGate &&
      'scoreReadyLabel' in currentBlock &&
      currentBlock.scoreReadyLabel) ||
    'Score ready';

  const raceConfig = resolveRaceConfig(readinessRace);

  // The pre-verdict race state (post-GW1): drives the email-gate banner
  // subtext variants once a real league has been selected.
  const liveRaceState = state.selectedLeague
    ? deriveRaceMetrics(state.selectedLeague, raceConfig).state
    : null;

  const banner = getTopBanner(currentBlock);
  const emailStateSubtext =
    isPostGw1 &&
    liveRaceState &&
    currentBlock._type === 'readinessEmailGateBlock'
      ? liveRaceState === 'behind'
        ? currentBlock.bannerSubtextBehind
        : liveRaceState === 'level'
        ? currentBlock.bannerSubtextLevel
        : currentBlock.bannerSubtextAhead
      : undefined;
  const resolvedBanner =
    banner && emailStateSubtext
      ? { ...banner, subtext: emailStateSubtext }
      : banner;

  const renderStepBlock = (block: SanityQuizBlock) => {
    switch (block._type) {
      case 'readinessTeamImportBlock':
        return (
          <TeamImportBlock
            key={block._key}
            {...block}
            onOcrImported={() =>
              // OCR carries no FPL entry id, so the post-GW1 real-league
              // fetch is impossible — route to the league-type fallback.
              goToBlockType(
                isPostGw1 ? 'readinessStakesBlock' : 'readinessLeagueNameBlock'
              )
            }
            onEntrySelected={handleEntrySelected}
            onBuildTeam={() => goToBlockType('readinessTemplateTeamBlock')}
          />
        );

      case 'readinessTeamConfirmBlock':
        return (
          <TeamConfirmBlock
            key={block._key}
            {...block}
            onConfirm={goToNextStep}
            onSearchAgain={handleSearchAgain}
          />
        );

      case 'readinessTemplateTeamBlock':
        return (
          <TemplateTeamBlock
            key={block._key}
            {...block}
            onBuilt={() =>
              // Manually built squads have no FPL entry id either (3b)
              isPostGw1 ? goToBlockType('readinessStakesBlock') : goToNextStep()
            }
            onBack={() => goToBlockType('readinessTeamImportBlock')}
          />
        );

      case 'readinessLeagueNameBlock':
        return (
          <LeagueNameBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'readinessCaptainBlock':
        return (
          <CaptainBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'readinessTransferBlock':
        return (
          <TransferBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'readinessStakesBlock':
        return (
          <StakesBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'readinessEmailGateBlock':
        return (
          <EmailGateBlock
            key={block._key}
            {...block}
            onSubmit={handleEmailSubmit}
            isSubmitting={emailCaptureMutation.isLoading}
          />
        );

      case 'readinessProcessingBlock':
        return (
          <ProcessingBlock
            key={block._key}
            {...block}
            onComplete={handleProcessingComplete}
          />
        );

      case 'readinessVerdictBlock':
        return (
          <VerdictBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'readinessLeagueSelectBlock':
        return (
          <LeagueSelectBlock
            key={block._key}
            {...block}
            onSelected={goToNextStep}
            onFallback={() => goToBlockType('readinessStakesBlock')}
          />
        );

      case 'readinessRaceVerdictBlock':
        return (
          <RaceVerdictBlock key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'paywallBlock':
        return (
          <PaywallBlock
            key={block._key}
            {...block}
            theme={quizPage.theme}
            quizState={INITIAL_QUIZ_STATE}
            templateKey={templateKey}
            paywallContext={buildReadinessPaywallContext(state, isPostGw1)}
            loginHintEmail={capturedEmail ?? undefined}
            emailOnlySignup
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="relative z-20 min-h-screen bg-neutral-100">
      <FunnelHeader />
      <div className="sticky top-0 z-30">
        <StepProgressTopBar
          totalSteps={progressSteps.length}
          currentStep={currentProgressStep}
        />
        {isJeopardyBarVisible && (
          <ReadinessBar
            fillPercent={liveRace?.userPercent ?? liveReadiness?.score ?? 0}
            targetPercent={
              liveRace?.pacePercent ?? liveReadiness?.winnersTarget ?? 80
            }
            statusLabel={
              isEmailGate
                ? scoreReadyLabel
                : isPostGw1
                ? raceConfig.paceStatusLabel
                : BUILDING_SCORE_LABEL
            }
            markerLabel={isPostGw1 ? raceConfig.paceMarkerLabel : undefined}
            headerLabel={isPostGw1 ? 'Your race' : undefined}
            isReady={isEmailGate}
          />
        )}
      </div>
      <TopBanner banner={resolvedBanner} />
      {renderStepBlock(currentBlock)}
    </div>
  );
};

export default ReadinessFlowController;
